(() => {
  /* Provider-agnostic, consent-aware analytics event engine.
   * Emits standard commerce events to a data layer and a DOM event so any
   * platform (GA4, GTM, Meta, TikTok, Pinterest, etc.) can subscribe.
   * The engine carries no vendor-specific business logic. */

  const contextEl = document.getElementById('BcosAnalyticsContext');
  let context = {};
  try {
    context = contextEl ? JSON.parse(contextEl.textContent) : {};
  } catch (error) {
    context = {};
  }

  const dataLayer = window.dataLayer || (window.dataLayer = []);
  const listeners = new Set();

  /* ---------------------------------------------------------- */
  /* Consent (Shopify Customer Privacy API aware)                */
  /* ---------------------------------------------------------- */
  const ESSENTIAL = 'essential';
  const ANALYTICS = 'analytics';
  const MARKETING = 'marketing';

  function consentFor(category) {
    try {
      const api = window.Shopify?.consent;
      if (api && typeof api.checkConsent === 'function') {
        const granted = api.checkConsent({ category: category });
        if (typeof granted === 'boolean') return granted;
      }
      if (window.Shopify?.ConsentTracking && typeof window.Shopify.ConsentTracking.consentCollectionAllowed === 'function') {
        return window.Shopify.ConsentTracking.consentCollectionAllowed();
      }
    } catch (error) {
      /* consent API unavailable; default to allowed for essential handling */
    }
    return true;
  }

  /* ---------------------------------------------------------- */
  /* Core engine                                                 */
  /* ---------------------------------------------------------- */
  function formatPayload(name, data, category) {
    const now = new Date();
    return {
      event: name,
      category: category,
      timestamp: now.toISOString(),
      ...data,
      context: {
        page_type: context.page_type,
        path: context.path,
        url: context.url,
        currency: context.currency,
        money_format: context.money_format,
        customer_logged_in: context.customer_logged_in,
        customer_id: context.customer_id,
      },
    };
  }

  function track(name, data, category = ANALYTICS) {
    const payload = formatPayload(name, data || {}, category);

    if (category !== ESSENTIAL && !consentFor(category)) {
      window.BcosAnalytics._debug(`blocked (${category}): ${name}`);
      return;
    }

    dataLayer.push(payload);
    const domEvent = new CustomEvent('bcos:analytics', { detail: payload });
    document.dispatchEvent(domEvent);
    listeners.forEach((fn) => fn(payload));
    window.BcosAnalytics._debug(name, payload);
  }

  /* ---------------------------------------------------------- */
  /* Public API                                                  */
  /* ---------------------------------------------------------- */
  window.BcosAnalytics = {
    track,
    getContext: () => context,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    _debug() {
      if (window.BcosAnalytics.debug && window.console) {
        window.console.log('[bcos-analytics]', ...arguments);
      }
    },
  };

  const params = new URLSearchParams(window.location.search);
  window.BcosAnalytics.debug = context.debug === true || params.has('bcos_analytics_debug');

  /* ---------------------------------------------------------- */
  /* Standard page + product events                              */
  /* ---------------------------------------------------------- */
  track('page_view', { title: context.page_title }, ANALYTICS);

  if (context.product) {
    track('view_item', {
      currency: context.currency,
      value: context.product.price / 100,
      items: [{
        item_id: String(context.product.id),
        item_name: context.product.title,
        item_brand: context.product.vendor,
        item_category: context.product.product_type,
        price: context.product.price / 100,
        variant: context.product.sku,
      }],
    }, ANALYTICS);
  }

  if (context.collection) {
    track('view_item_list', {
      currency: context.currency,
      item_list_id: String(context.collection.id),
      item_list_name: context.collection.title,
    }, ANALYTICS);
  }

  if (context.search) {
    track('search', {
      search_term: context.search.query,
      results_count: context.search.results_count,
    }, ANALYTICS);
  }

  /* ---------------------------------------------------------- */
  /* Scroll depth (throttled, opt-in)                            */
  /* ---------------------------------------------------------- */
  if (context.scroll_tracking) {
    const marks = new Set();
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const doc = document.documentElement;
        const ratio = (window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight - window.innerHeight, 1);
        [25, 50, 75, 100].forEach((mark) => {
          if (!marks.has(mark) && ratio >= mark / 100) {
            marks.add(mark);
            track('scroll', { percent: mark }, ANALYTICS);
          }
        });
        ticking = false;
      });
    }, { passive: true });
  }
})();
